import { ReactNode, useEffect, useRef, useState } from 'react';
import type {
  AccountBillingAccount, AccountBillingView, PercentMonths, PlanChangeEffective, TrialGrantBody,
} from '../../api/platform';
import { Modal } from '../../components/fs';
import type { AccountTrial, Plan } from '../../shared/types';
import { isoPlusDays, longDate, money, shortMDY, todayIso } from './planDisplay';
import { usePlatform } from './PlatformLayout';

/**
 * The money modals of one account's billing page (R12, screens 38–39).
 *
 * Every action that moves money opens one of these, and each says what will
 * happen — the charge, the date, where the account lands — before the button
 * that does it (contract rule 8, D3–D5). They render nothing of their own
 * state into the page: the page opens one at a time and owns what they post.
 */

export type Interval = 'month' | 'year';

/**
 * The trial row as the server sends it. `listTrials` returns the restore
 * point's id and the day the account had already paid through (054) as well
 * as the names; the shared `AccountTrial` type does not declare them yet.
 */
export type TrialRow = AccountTrial & { previous_plan_id?: number | null; paid_through?: string | null };

/** "/mo" or "/yr" after a price. */
export const perShort = (i: Interval) => (i === 'year' ? '/yr' : '/mo');
/** "a month" / "a year", for sentences that name a recurring price. */
export const perWords = (i: Interval) => (i === 'year' ? 'a year' : 'a month');
/** A plan's list price at an interval. */
export const priceAt = (p: Plan | null | undefined, i: Interval): number | null =>
  (!p ? null : i === 'year' ? p.price_annual_cents : p.price_monthly_cents);

/** "$50/mo", "free", or '' when there is no price to name. */
export function priceText(cents: number | null | undefined, i: Interval, currency = 'usd'): string {
  if (cents === null || cents === undefined) return '';
  if (cents === 0) return 'free';
  return `${money(cents, currency)}${perShort(i)}`;
}

/** "15% off for 12 months" / "15% off, forever" — the coupon's own name (D4). */
export function percentWords(pct: number, months: number | null): string {
  return `${pct}% off${months ? ` for ${months} month${months === 1 ? '' : 's'}` : ', forever'}`;
}

/** Whether the percent-off is still taking money off — set, and not past its end. */
export function percentRunning(a: AccountBillingAccount, now = new Date()): boolean {
  return a.discount_percent !== null && a.discount_percent !== undefined
    && (!a.discount_ends_at || new Date(a.discount_ends_at).getTime() > now.getTime());
}

/**
 * Whether Stripe holds the standing free period — a subscription it is
 * trialing. Extend and End at expiry write to Stripe only then (the server's
 * `updateTrialStripe`, the same check `revokeTrialStripe` makes): a period
 * that stood locally over a paying subscription leaves that subscription alone.
 */
export const stripeHoldsPeriod = (view: AccountBillingView): boolean =>
  view.stripe.connected && !!view.account.stripe_subscription_id && view.account.subscription_status === 'trialing';

/** "Sep 18, 2026" — a tile's or a row's date. */
export const monDY = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** "Sep 18" — the account page's pills and tiles (074), where the year goes without saying. */
export const monD = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** A calendar day plus whole calendar months, the 31st clamped to the month's last day. */
export function plusMonths(iso: string, months: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Which interval a free period converts at — the server's own rule
 * (`grantTrialSubscription`): annual only when the account is annual AND the
 * plan has an annual price.
 */
export const convertInterval = (a: AccountBillingAccount, p: Plan | null | undefined): Interval =>
  (a.billing_interval === 'year' && !!p?.stripe_price_annual ? 'year' : 'month');

/** What converting to `p` charges, "$100/mo". */
export const convertPrice = (a: AccountBillingAccount, p: Plan | null | undefined): string => {
  const i = convertInterval(a, p);
  return priceText(priceAt(p, i), i, p?.currency);
};

/**
 * Whether Stripe would own a free period of `p` — a key in force and a Stripe
 * price for the interval the grant would use. Without one the server keeps
 * the period locally ("push the plan first"), and nothing at Stripe ends.
 */
export function stripeBacks(view: AccountBillingView, p: Plan | null | undefined): boolean {
  if (!view.stripe.connected || !p) return false;
  const annual = view.account.billing_interval === 'year' && !!p.stripe_price_annual;
  return !!(annual ? p.stripe_price_annual : p.stripe_price_monthly);
}

/**
 * Where a free period puts the account back: the restore point the grant
 * recorded (054), priced at the account's interval — its agreed price when
 * one is set, since an override belongs to the account, not the plan.
 *
 * `plans` carries only the plans still on sale, so a grandfathered (retired)
 * restore plan is not in it; the server sends that one beside the list as
 * `restorePlan`, and it is read only when the list has no match.
 */
export function restorePoint(view: AccountBillingView, trial: TrialRow) {
  const { account } = view;
  const retired = view.restorePlan
    && (trial.previous_plan_id != null
      ? view.restorePlan.id === trial.previous_plan_id
      : !!trial.previous_plan_key && view.restorePlan.key === trial.previous_plan_key)
    ? view.restorePlan : undefined;
  const plan = (trial.previous_plan_id != null ? view.plans.find((p) => p.id === trial.previous_plan_id) : undefined)
    ?? (trial.previous_plan_key ? view.plans.find((p) => p.key === trial.previous_plan_key) : undefined)
    ?? retired
    ?? null;
  const cents = account.price_override_cents ?? priceAt(plan, account.billing_interval);
  return {
    name: trial.previous_plan_name ?? null,
    plan,
    price: plan || account.price_override_cents !== null
      ? priceText(cents, account.billing_interval, plan?.currency ?? view.plan.currency) : '',
  };
}

/**
 * The standing period's own plan. The list leaves retired plans out; during a
 * Stripe-backed period the account's plan IS the period's plan, so that is
 * the fallback.
 */
export function trialPlanOf(view: AccountBillingView, trial: TrialRow): Plan | undefined {
  return view.plans.find((p) => p.id === trial.plan_id) ?? (view.plan.id === trial.plan_id ? view.plan : undefined);
}

/**
 * Whether a standing period that does not convert takes the subscription with
 * it. The grant (`grantTrialSubscription`) moves or makes a subscription, set
 * to cancel at the period's end, only when the period's plan has a Stripe
 * price for the interval it uses — `stripeBacks`, the rule the grant's own
 * confirm read. Without one the period stood locally, the subscription was
 * never touched, and the entitlement falls back to the restore point.
 */
export function periodEndsSubscription(view: AccountBillingView, trial: TrialRow): boolean {
  return !trial.auto_convert && !!view.account.stripe_subscription_id && stripeBacks(view, trialPlanOf(view, trial));
}

/**
 * How a STANDING free period ends, said as the server behaves (D3, R11's
 * call): it converts; or it does not while Stripe holds a subscription for
 * it, which the grant set to cancel, and the account moves to Free; or Stripe
 * holds nothing for it and the entitlement falls back to the restore point.
 */
export function trialEnding(view: AccountBillingView, trial: TrialRow): string {
  if (trial.auto_convert) {
    const amount = convertPrice(view.account, trialPlanOf(view, trial));
    return `Then ${trial.plan_name}${amount ? ` at ${amount}` : ''}, charged to the card on file.`;
  }
  if (periodEndsSubscription(view, trial)) {
    return 'Then their subscription ends and the account moves to Free.';
  }
  const back = restorePoint(view, trial);
  return back.name
    ? `Then they go back to ${back.name}${back.price ? ` at ${back.price}` : ''}.`
    : 'Then they go back to their paid plan.';
}

/**
 * What End now does at Stripe — `revokeTrialStripe`'s own branches, in its
 * order, so the confirm, the End now row and the toast say one thing:
 *
 * - `local`        no key in force or no subscription: nothing at Stripe moves.
 * - `untouched`    a subscription Stripe is not trialing: the revoke leaves it
 *                  alone (the period stood locally), so their renewal stays.
 * - `cancels`      the restore plan has no Stripe price at the account's OWN
 *                  interval (`priceIdFor`, no monthly fallback — unlike the
 *                  grant): the subscription is cancelled, and the webhook
 *                  moves the account to Free.
 * - `paidAhead`    days already bought run past now: the trial ends then, and
 *                  that is the next charge.
 * - `chargesToday` nothing bought is left: the trial ends now, and Stripe
 *                  charges the restore plan today.
 */
export type EndNowOutcome = 'local' | 'untouched' | 'cancels' | 'paidAhead' | 'chargesToday';

export function endNowOutcome(view: AccountBillingView, trial: TrialRow, now = new Date()): EndNowOutcome {
  const { account, stripe } = view;
  if (!stripe.connected || !account.stripe_subscription_id) return 'local';
  if (account.subscription_status !== 'trialing') return 'untouched';
  const back = restorePoint(view, trial).plan;
  if (back && !(account.billing_interval === 'year' ? back.stripe_price_annual : back.stripe_price_monthly)) {
    return 'cancels';
  }
  const paidThrough = trial.paid_through ? new Date(trial.paid_through).getTime() : Number.NaN;
  return paidThrough > now.getTime() ? 'paidAhead' : 'chargesToday';
}

/** "monthly" / "annual" — the Stripe price a revoke looks for. */
export const priceWord = (i: Interval) => (i === 'year' ? 'annual' : 'monthly');

/** The one error line a modal shows — the server's own sentence. */
function ModalError({ error }: { error: string }) {
  return error ? <div className="ad-err" role="alert">{error}</div> : null;
}

// ---------------------------------------------------------------------------
// Percent off (D4)
// ---------------------------------------------------------------------------

/** Take the percent-off away — a danger confirm naming what they pay after. */
export function RemovePercentModal({ view, busy, error, onConfirm, onClose }: {
  view: AccountBillingView;
  busy: boolean;
  error: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { account, price } = view;
  return (
    <Modal
      title="Remove the percent off?"
      narrow
      className="pl-ab-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={busy} onClick={onConfirm}>
            {busy ? 'Removing…' : 'Remove discount'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="fs-consequence warn">
        Their <b>{percentWords(account.discount_percent ?? 0, account.discount_months)}</b> ends now
        {price.cents ? <>: from the next invoice they pay {money(price.cents, price.currency)}{perShort(price.interval)}
          {price.overridden ? ', their agreed price' : ''}</> : null}.
        To give it back you add a new discount, and its months start again.
      </div>
      <ModalError error={error} />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Free period: start / replace, extend, end now (D3)
// ---------------------------------------------------------------------------

/** What the grant form is holding before anyone commits to it. */
export interface TrialDraft {
  planId: number;
  /** the selected preset, or null once the date was typed by hand */
  len: 14 | 30 | 60 | 90 | null;
  startsAt: string;
  expiresAt: string;
  cardRequired: boolean;
  autoConvert: boolean;
  notify: boolean;
  note: string;
}

const PRESETS = [14, 30, 60, 90] as const;

/** Plans above the current one, marked available for trials, cheapest step first. */
export function trialablePlans(v: AccountBillingView): Plan[] {
  return v.plans
    .filter((p) => p.sort_order > v.plan.sort_order && p.trialable && !p.retired_at && p.price_monthly_cents !== null)
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** The defaults: the next plan up, 30 days from today, card required, converting, emailed. */
export function freshDraft(v: AccountBillingView): TrialDraft {
  const start = todayIso();
  return {
    planId: trialablePlans(v)[0]?.id || 0,
    len: 30,
    startsAt: start,
    expiresAt: isoPlusDays(start, 30),
    cardRequired: true,
    autoConvert: true,
    notify: true,
    note: '',
  };
}

/**
 * The read-back: what is about to happen, generated from the form state so it
 * recomputes on every change, and ending the way the server will end it (D3).
 */
function GrantSentence({ view, trialPlan, draft }: { view: AccountBillingView; trialPlan: Plan; draft: TrialDraft }) {
  const { account, plan, price } = view;
  const changes: string[] = [];
  if (trialPlan.features?.sso && !plan.features?.sso) changes.push('SSO access unlocks immediately');
  if (trialPlan.limits.forms !== plan.limits.forms) {
    const fmt = (v: number | null) => (v === null ? '∞' : String(v));
    changes.push(`their form limit goes ${fmt(plan.limits.forms)} → ${fmt(trialPlan.limits.forms)}`);
  }
  const backed = stripeBacks(view, trialPlan);
  const pct = percentRunning(account) ? account.discount_percent : null;
  const back = priceText(price.cents, price.interval, price.currency);
  return (
    <>
      {account.name} gets <b>{trialPlan.name}</b> free from <b>{shortMDY(draft.startsAt)}</b> to{' '}
      <b>{longDate(draft.expiresAt)}</b>.{' '}
      {draft.autoConvert
        ? <>Then {trialPlan.name} at <b>{convertPrice(account, trialPlan)}</b>, charged to the card on file.</>
        : backed
          ? <>Then their subscription ends and the account moves to Free.</>
          : <>Then they go back to {plan.name}{back ? <> at {back}</> : null}.</>}
      {changes.length > 0 && <> {changes.join('; ')}.</>}
      {pct !== null && (draft.autoConvert || !backed
        ? <> Their {pct}% discount carries on and applies after the free period.</>
        : <> Their {pct}% discount ends with it.</>)}
    </>
  );
}

/**
 * Start a free upgrade period, or replace the standing one. Upgrade-only
 * plans, the next one up by default; the presets write the expiry field and a
 * typed date lets go of them.
 */
export function GrantModal({ view, replacing, busy, error, onSubmit, onClose }: {
  view: AccountBillingView;
  /** the active period this one replaces, if any */
  replacing: AccountTrial | null;
  busy: boolean;
  error: string;
  onSubmit: (body: TrialGrantBody) => void;
  onClose: () => void;
}) {
  const { account, plan } = view;
  // How far ahead the reminder goes is a platform rule (Platform › Rules).
  const reminderDays = usePlatform().rules?.trialReminderDaysBefore ?? 7;
  const trialable = trialablePlans(view);
  const [draft, setDraft] = useState<TrialDraft>(() => freshDraft(view));
  const setD = (patch: Partial<TrialDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const trialPlan = trialable.find((p) => p.id === draft.planId) || trialable[0];
  const backed = stripeBacks(view, trialPlan);

  /** The presets write the expiry field; the date is the stored value. */
  const pickLen = (len: 14 | 30 | 60 | 90) => setD({ len, expiresAt: isoPlusDays(draft.startsAt, len) });
  // A live preset keeps its promise when the start moves; a typed date stays.
  const setStarts = (startsAt: string) =>
    setD(draft.len ? { startsAt, expiresAt: isoPlusDays(startsAt, draft.len) } : { startsAt });
  const setExpires = (expiresAt: string) => setD({ expiresAt, len: null });

  return (
    <Modal
      title={replacing ? 'Replace the free period' : 'Start a free period'}
      wide
      className="pl-ab-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={busy || !trialPlan}
            onClick={() => onSubmit({
              planId: draft.planId || trialPlan!.id,
              startsAt: draft.startsAt,
              expiresAt: draft.expiresAt,
              cardRequired: draft.cardRequired,
              autoConvert: draft.autoConvert,
              notify: draft.notify,
              note: draft.note,
            })}>
            {busy ? 'Granting…' : 'Grant free period'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="st-muted pl-ab-lede">
        They get the chosen plan’s features without paying until the end date. One period at a time.
      </div>
      <div className="fs-field">
        <label htmlFor="ab-trial-plan">Plan during the free period</label>
        <select id="ab-trial-plan" className="pl-hl" value={draft.planId} onChange={(e) => setD({ planId: Number(e.target.value) })}>
          {trialable.map((p) => (
            <option key={p.id} value={p.id}>{p.name} — {money(p.price_monthly_cents, p.currency)}/month after</option>
          ))}
        </select>
        <div className="hint">Only plans above their current one, and only those marked available for trials.</div>
      </div>
      <div className="fs-field">
        <span className="pl-ab-label" id="ab-len">Length</span>
        <div className="pl-presets" role="group" aria-labelledby="ab-len">
          {PRESETS.map((n) => (
            <button key={n} type="button" className={`pl-preset${draft.len === n ? ' on' : ''}`} onClick={() => pickLen(n)}>
              {n} days
            </button>
          ))}
        </div>
      </div>
      <div className="fs-row-2">
        <div className="fs-field">
          <label htmlFor="ab-trial-start">Starts</label>
          <input id="ab-trial-start" type="date" value={draft.startsAt} onChange={(e) => setStarts(e.target.value)} />
        </div>
        <div className="fs-field">
          <label htmlFor="ab-trial-end">Ends on — end of day, their timezone</label>
          <input id="ab-trial-end" type="date" className="pl-hl" value={draft.expiresAt} onChange={(e) => setExpires(e.target.value)} />
        </div>
      </div>
      <div className="hint st-muted pl-ab-hint">
        Set by a preset, or type any date — the presets are a shortcut, not a constraint.
      </div>

      <div className="pl-ab-checks">
        <label className="fs-check">
          <input type="checkbox" checked={draft.cardRequired}
            onChange={(e) => setD(e.target.checked
              ? { cardRequired: true }
              // Without a card the conversion charge cannot run.
              : { cardRequired: false, autoConvert: false })} />
          <span>
            <b>Require a card before the period starts</b>
            <small>They finish Stripe Checkout in $0 trial mode. Without this the conversion below cannot run.</small>
          </span>
        </label>
        <label className={`fs-check${draft.cardRequired ? '' : ' pl-ab-off'}`}>
          <input type="checkbox" checked={draft.autoConvert} disabled={!draft.cardRequired}
            onChange={(e) => setD({ autoConvert: e.target.checked })} />
          <span>
            <b>Convert to paid at expiry</b>
            <small>
              First charge {money(priceAt(trialPlan, convertInterval(account, trialPlan)), trialPlan?.currency)} on{' '}
              <b>{longDate(draft.expiresAt)}</b> unless they cancel. Untick and{' '}
              {backed ? 'their subscription ends at expiry and the account moves to Free' : `they go back to ${plan.name}`}.
            </small>
          </span>
        </label>
        <label className="fs-check">
          <input type="checkbox" checked={draft.notify} onChange={(e) => setD({ notify: e.target.checked })} />
          <span>
            <b>Email them now, and {reminderDays} days before expiry</b>
            <small>Both emails name the plan, the date and the amount that will be charged.</small>
          </span>
        </label>
      </div>

      <div className="fs-field">
        <label htmlFor="ab-trial-note">Why — internal, never shown to the customer</label>
        <textarea id="ab-trial-note" value={draft.note} onChange={(e) => setD({ note: e.target.value })} />
      </div>

      {trialPlan && (
        <div className="pl-confirm">
          <GrantSentence view={view} trialPlan={trialPlan} draft={draft} />
        </div>
      )}
      {replacing && (
        <div className="pl-note">
          This replaces the active {replacing.plan_name} period, which ends when this one is granted.
        </div>
      )}
      <ModalError error={error} />
    </Modal>
  );
}

/** Move the standing period's end — presets from the current end, or any date. */
export function ExtendModal({ view, trial, busy, error, onSubmit, onClose }: {
  view: AccountBillingView;
  trial: TrialRow;
  busy: boolean;
  error: string;
  onSubmit: (expiresAt: string) => void;
  onClose: () => void;
}) {
  const current = trial.expires_at.slice(0, 10);
  const [to, setTo] = useState(isoPlusDays(current, 14));
  const [len, setLen] = useState<number | null>(14);
  const inFuture = to > todayIso();
  const longer = to > current;
  const shift = Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${current}T00:00:00`).getTime()) / 86400000);

  return (
    <Modal
      title="Extend the free period"
      className="pl-ab-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={busy || !to || !inFuture || to === current} onClick={() => onSubmit(to)}>
            {busy ? 'Saving…' : longer ? 'Extend' : 'Shorten'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="st-muted pl-ab-lede">
        {trial.plan_name} is free until {longDate(current)}.{' '}
        {/* The server writes to Stripe only while it holds the period. */}
        {stripeHoldsPeriod(view)
          ? 'Stripe moves the end with it.'
          : 'Stripe is not holding this period, so nothing changes at Stripe.'}
      </div>
      <div className="fs-field">
        <span className="pl-ab-label" id="ab-ext-len">Add</span>
        <div className="pl-presets" role="group" aria-labelledby="ab-ext-len">
          {PRESETS.map((n) => (
            <button key={n} type="button" className={`pl-preset${len === n ? ' on' : ''}`}
              onClick={() => { setLen(n); setTo(isoPlusDays(current, n)); }}>
              +{n} days
            </button>
          ))}
        </div>
      </div>
      <div className="fs-field">
        <label htmlFor="ab-ext-to">Ends on</label>
        <input id="ab-ext-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setLen(null); }} />
      </div>
      {to && !inFuture && <div className="pl-ab-blocker">Pick a day after today — a free period cannot end in the past.</div>}
      {to && inFuture && to !== current && (
        <div className="fs-consequence">
          {trial.plan_name} stays free until <b>{longDate(to)}</b>, {Math.abs(shift)} day{Math.abs(shift) === 1 ? '' : 's'}{' '}
          {longer ? 'longer' : 'sooner'} than now. {trialEnding(view, trial)}
        </div>
      )}
      <ModalError error={error} />
    </Modal>
  );
}

/**
 * End the period now (D3): a danger confirm naming the plan and price they go
 * back to and when they are next charged. What it does is unchanged — the
 * restore point, the days already paid kept — and the sentence follows the
 * server's branches (`endNowOutcome`), so it never promises a charge Stripe
 * will not make, or a plan the account will not land on.
 */
export function EndNowModal({ view, trial, busy, error, onConfirm, onClose }: {
  view: AccountBillingView;
  trial: TrialRow;
  busy: boolean;
  error: string;
  onConfirm: (note: string) => void;
  onClose: () => void;
}) {
  const { account, plan } = view;
  const [note, setNote] = useState('');
  const back = restorePoint(view, trial);
  // The server's own branches (`revokeTrialStripe`), shared with the End now row.
  const outcome = endNowOutcome(view, trial);

  let next: ReactNode = null;
  if (outcome === 'paidAhead') next = <>Their next charge is <b>{longDate(trial.paid_through!)}</b>.</>;
  else if (outcome === 'chargesToday') next = <>They have no paid period left, so Stripe starts their paid plan today and charges it now.</>;
  else if (outcome === 'untouched') next = <>Stripe is not holding this period, so nothing is charged and their renewal stays as it is.</>;
  else if (outcome === 'local') next = <>There is no Stripe subscription behind this period, so nothing is charged.</>;

  return (
    <Modal
      title="End the free period now?"
      className="pl-ab-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={busy} onClick={() => onConfirm(note.trim())}>
            {busy ? 'Ending…' : 'End free period'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      {back.name && outcome === 'cancels' ? (
        // The restore plan has no Stripe price at their interval: nothing is
        // charged, the subscription is cancelled, and they land on Free — not
        // on the plan the period came from.
        <div className="fs-consequence warn">
          Their subscription is cancelled now — Stripe has no {priceWord(account.billing_interval)} price
          for <b>{back.name}</b> — so nothing is charged and the account moves to <b>Free</b>. Anything over
          the Free limits goes read-only; nothing is deleted.
        </div>
      ) : back.name ? (
        <div className="fs-consequence warn">
          They go back to <b>{back.name}</b>{back.price ? ` (${back.price})` : ''} now. {next} Anything over
          the {back.name} limits goes read-only; nothing is deleted.
        </div>
      ) : (
        <div className="fs-consequence warn">
          This period was granted before the plan it came from was recorded, so ending it now drops them to{' '}
          <b>{plan.name}</b> — the plan of the period itself — and bills it immediately. Set the plan by hand
          instead if that is not what you want.
        </div>
      )}
      <div className="fs-field">
        <label htmlFor="ab-end-note">Note — kept with the record (optional)</label>
        <input id="ab-end-note" value={note} placeholder="Why? Kept with the record." onChange={(e) => setNote(e.target.value)} />
      </div>
      <ModalError error={error} />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// One-time credit (D5)
// ---------------------------------------------------------------------------

/**
 * The ceiling on a one-time credit, mirrored from the server's MAX_CREDIT_DAYS.
 * Thirty days of any monthly plan fits inside one invoice, which is the whole
 * reason a bounded credit replaced an open-ended free period.
 */
export const MAX_CREDIT_DAYS = 30;

/** Why the credit cannot be issued yet, or null — the billing email first, as it is always true. */
export function creditBlockerOf(account: AccountBillingAccount, creditDays: string, creditReason: string): string | null {
  const n = creditDays.trim() ? Number(creditDays) : null;
  const valid = n !== null && Number.isInteger(n) && n >= 1 && n <= MAX_CREDIT_DAYS;
  if (!account.billing_email) return 'Set a billing email first — a credit is emailed to the customer.';
  if (!creditDays.trim()) return null;
  if (!valid) return `Between 1 and ${MAX_CREDIT_DAYS} whole days.`;
  if (!creditReason.trim()) return 'A reason is required — someone will ask why this invoice was short.';
  return null;
}

/** Issue (or replace) the one-time credit: days and a reason, and what they are worth. */
export function CreditModal({ view, creditDays, creditReason, setCreditDays, setCreditReason, busy, error, onSubmit, onClose }: {
  view: AccountBillingView;
  creditDays: string;
  creditReason: string;
  setCreditDays: (v: string) => void;
  setCreditReason: (v: string) => void;
  busy: boolean;
  error: string;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const { account, plan, price } = view;
  const creditN = creditDays.trim() ? Number(creditDays) : null;
  const creditValid = creditN !== null && Number.isInteger(creditN) && creditN >= 1 && creditN <= MAX_CREDIT_DAYS;
  // Priced the way the server prices it, against a nominal period — the exact
  // figure comes back from Stripe; this is the sanity check before it. The
  // credit comes off the invoice after a running percent, so it is worth days
  // of what they pay after that percent, never of the price before it. One
  // discount at a time: beside an agreed price the server holds a percent back,
  // so the credit is days of the agreed price alone.
  const pctOn = percentRunning(account) && account.price_override_cents === null ? account.discount_percent! : null;
  const paysCents = price.cents !== null && pctOn ? Math.round((price.cents * (100 - pctOn)) / 100) : price.cents;
  const creditWorth = creditValid && paysCents !== null
    ? Math.round((paysCents / (account.billing_interval === 'year' ? 365 : 30)) * creditN!)
    : null;
  const blocker = creditBlockerOf(account, creditDays, creditReason);
  const unchanged = creditN === account.credit_days && creditReason.trim() === (account.credit_reason || '');

  return (
    <Modal
      title={account.credit_days ? 'Change the one-time credit' : 'Add a one-time credit'}
      className="pl-ab-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" onClick={onSubmit} disabled={busy || !!blocker || !creditValid || unchanged}>
            {busy ? 'Issuing…' : 'Issue credit'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="st-muted pl-ab-lede">
        Up to {MAX_CREDIT_DAYS} days of this account’s own price, taken off their next invoice as a named
        discount. To give them a higher plan free instead, start a free period.
      </div>
      <div className="fs-row-2 pl-ab-credit-row">
        <div className="fs-field">
          <label htmlFor="credit-days">Days of credit</label>
          <input id="credit-days" type="number" min={1} max={MAX_CREDIT_DAYS} value={creditDays}
            placeholder="14" onChange={(e) => setCreditDays(e.target.value)} />
        </div>
        <div className="fs-field">
          <label htmlFor="credit-reason">Reason</label>
          <input id="credit-reason" value={creditReason}
            placeholder="Onboarding credit, outage goodwill, agreed with sales…"
            onChange={(e) => setCreditReason(e.target.value)} />
        </div>
      </div>
      {/* What it is worth, before it is issued. */}
      {creditWorth !== null && (
        <div className="pl-note">
          <b>{creditN} day{creditN === 1 ? '' : 's'} is {money(creditWorth, plan.currency)}</b> off their
          next invoice — {money(paysCents, plan.currency)} {perWords(account.billing_interval)}
          {pctOn
            ? <>, what they pay after their {pctOn}% off {price.overridden ? 'their agreed' : 'the'} {money(price.cents, plan.currency)}</>
            : price.overridden && <>, their agreed price</>}, divided by the days in a billing period.
          The customer sees it as a line reading &ldquo;Account credit&rdquo;, and is emailed.
        </div>
      )}
      {account.credit_days && (
        <div className="pl-note">This replaces the {account.credit_days}-day credit outstanding.</div>
      )}
      {blocker && <div className="pl-ab-blocker">{blocker}</div>}
      <ModalError error={error} />
    </Modal>
  );
}
